import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useReducedMotion } from "./useReducedMotion.js";
import { MOTION_DURATION_MS } from "../motion.js";

/* Retains rows that have left the source list long enough for them to collapse
   (ADR-0091). Without this a removed row vanishes between two frames and every
   row below it jumps up under the pointer — the list equivalent of an overlay
   that has no exit.

   Generalised from `molecules/Toast/toast-presence.ts`, which solved exactly
   this for the toast stack: keep a vanished entry in place, in order, until its
   exit has finished. The difference is that a toast reports its own `onExited`
   while an ordinary row does not, so removal is timed here instead.

   ORDER IS PRESERVED: a leaving row keeps its slot, so the list never reflows
   around the gap it is still closing. */

export interface PresentRow<T> {
  readonly item: T;
  readonly key: string;
  /** False while the row is collapsing; drives `data-motion-state`. */
  readonly open: boolean;
}

/**
 * The render list: every live row in the SOURCE's order, with the rows that
 * have gone put back where they sat so they collapse in place.
 *
 * The order has to come from the source, not from the previous render. An
 * earlier version walked the previous rows first and appended whatever the
 * source had added afterwards, which quietly meant "new rows go last" — so a
 * notification feed served newest-first rendered a newly arrived item at the
 * BOTTOM of its day group, and a source-side reorder did nothing at all
 * because every existing key was re-emitted in its old position.
 *
 * A leaving row has no place in the source, so it is anchored to the nearest
 * row that preceded it and is still live. That keeps a run of adjacent
 * departures in their own order, and puts a row that led the list back at the
 * front rather than at the end.
 */
function reconcile<T>(
  previous: readonly PresentRow<T>[],
  source: readonly T[],
  keyOf: (item: T) => string,
): readonly PresentRow<T>[] {
  const live = new Set(source.map(keyOf));
  // A row that came back while it was leaving reopens, because the source is
  // what decides `open` and it is in the source again.
  const next: PresentRow<T>[] = source.map((item) => ({
    item,
    key: keyOf(item),
    open: true,
  }));

  // Backwards, so two adjacent departures anchored to the same live row keep
  // their relative order: the later one is placed first and the earlier one
  // then lands in front of it.
  for (let i = previous.length - 1; i >= 0; i -= 1) {
    const row = previous[i]!;
    if (live.has(row.key)) continue;
    let at = 0;
    for (let j = i - 1; j >= 0; j -= 1) {
      const before = previous[j]!;
      if (!live.has(before.key)) continue;
      at = next.findIndex((candidate) => candidate.key === before.key) + 1;
      break;
    }
    next.splice(at, 0, { ...row, open: false });
  }
  return next;
}

export interface ListPresence<T> {
  readonly rows: readonly PresentRow<T>[];
  /** Drop a row early — for a caller whose row reports its own transitionend. */
  readonly onExited: (key: string) => void;
}

/**
 * Mirrors a source list into a render list where removed entries linger, in
 * order, until their exit transition has run.
 *
 * Reduced-motion callers never wait: a removed row is dropped on the same
 * render, matching {@link useTransitionPresence}.
 */
export function useListPresence<T>(
  source: readonly T[],
  keyOf: (item: T) => string,
  exitDurationMs: number = MOTION_DURATION_MS.state,
): ListPresence<T> {
  const reducedMotion = useReducedMotion();

  const [state, setState] = useState<{
    source: readonly T[];
    reducedMotion: boolean;
    rows: readonly PresentRow<T>[];
  }>(() => ({
    source,
    reducedMotion,
    rows: reconcile([], source, keyOf),
  }));

  // A source change is a state-machine event, so it is derived during render
  // rather than in an effect: a row must never be painted as present for one
  // frame after the data says it is gone.
  if (state.source !== source || state.reducedMotion !== reducedMotion) {
    const rows = reconcile(state.rows, source, keyOf);
    setState({
      source,
      reducedMotion,
      rows: reducedMotion ? rows.filter((row) => row.open) : rows,
    });
  }

  const onExited = useCallback((key: string) => {
    setState((current) =>
      current.rows.some((row) => row.key === key && !row.open)
        ? { ...current, rows: current.rows.filter((row) => row.key !== key) }
        : current,
    );
  }, []);

  const closingKeys = state.rows
    .filter((row) => !row.open)
    .map((row) => row.key)
    .join("|");

  useEffect(() => {
    if (!closingKeys) return;
    const timeout = window.setTimeout(() => {
      setState((current) => ({
        ...current,
        rows: current.rows.filter((row) => row.open),
      }));
    }, exitDurationMs);
    return () => window.clearTimeout(timeout);
  }, [closingKeys, exitDurationMs]);

  return { rows: state.rows, onExited };
}

/**
 * The stagger delay for the row at `index`, as the custom property
 * `pb-enter-row` reads. Capped so a long list never makes the last row wait —
 * and zeroed by the reduced-motion block in `theme.css`.
 */
export function enterDelay(
  index: number,
  stepMs = 20,
  capMs = 200,
): CSSProperties {
  /* Typed as CSSProperties, not as its own shape: a bare
     `{"--pb-enter-delay": string}` trips @types/react's weak-type check at
     every `style={enterDelay(i)}`, and asking two dozen call sites for a cast
     is asking for the cast to be copied somewhere it does not belong. */
  return {
    "--pb-enter-delay": `${Math.min(index * stepMs, capMs)}ms`,
  } as CSSProperties;
}
